import { Outlet, useSearchParams, useLocation } from 'react-router-dom';
import React, { useEffect, useState, createContext } from 'react';

import { Icon } from '../../components';
import {
    SelectedItemAsset,
    SelectedItemExtended,
    SelectedItemLegalType,
} from '../../components/SelectionPopup/SelectionPopup';
import { useDiagramStore } from '../../store/useDiagramStore';
import { Icons } from '../../components/Icon/Icon';
import { LinkedButton } from '../../components/Button';
import getDealTaxesUrlParams from '../../utils/get-deal-taxes-url-params';
import { GraphTypes } from '../../types/general';
import { DiagramData, SankeyData } from '../../types/deal-taxes';
import DealTaxesAsideMenu from './components/DealTaxesAsideMenu';
import { createPortal } from 'react-dom';
import { dispatchAsidePortalContentChanged } from '../../utils/aside-portal-event';

export interface DealTaxesSelectedData {
    selectedInvestor: SelectedItemExtended | undefined;
    assets: SelectedItemAsset[];
    investors: SelectedItemExtended[];
    investmentFund: SelectedItemLegalType;
    fundOfFunds: undefined | SelectedItemLegalType;

    openEnded: boolean;
    // corporate: boolean;
    booked?: boolean;
    ifBusinessAssets?: boolean;
    ifCommercialPartnership?: boolean;
    fofOpenEnded?: boolean;
    // fofCorporate?: boolean;
    fofIfCommercialPartnership?: boolean;
    ukInvesting?: boolean;
    fofUkInvesting?: boolean;
    // for optimisation
    fundStatus?: string;
}

export interface DealTaxesLayoutContextType {
    // Info props (lines 552-555)
    data: any; // rawData?.nav
    initialInvestment: number;
    returnOnAsset: number;
    distribution: number;
    inAside: boolean;

    // Menu props (lines 566-591)
    selectedData: DealTaxesSelectedData;
    setSelectedData: any;
    setSelectedInvestor: (investor: SelectedItemExtended) => void;
    active: boolean;
    hidden: boolean;
    onEdit: () => void;
    setOnEdit: (handler: () => void) => void; // Allow child components to override onEdit
    backButton: React.ReactNode;

    // Popup control
    showPopup: boolean;
    setShowPopup: React.Dispatch<React.SetStateAction<boolean>>;

    // Additional props from checksProps
    handleCheckbox: (prop: any) => (event: any) => Promise<void>;
    isFoF: boolean;
    fofLegalTypesDisabled: boolean | undefined;
    fofOpenEndedDisabled: boolean | undefined;
    isOptimisation: boolean;

    checkClicked: boolean;
    setCheckClicked: any;

    toggleRequest: boolean;
    setToggleRequest: any;

    // Route info
    isExpandedView: boolean;

    // Fund and optimisation
    fund: {
        country: string;
        fund: string;
    };
    optimisationUrl: string;

    // Page state
    pageReady: boolean;
    setPageReady: React.Dispatch<React.SetStateAction<boolean>>;
    tableUrl: string;
    setTableUrl: React.Dispatch<React.SetStateAction<string>>;

    setFofLegalTypesDisabled: any;
    setFofOpenEndedDisabled: any;

    rawData: DiagramData | null;
    setRawData: any;
    // Data fetching
    // getData: (changeCheckboxValue: boolean) => Promise<void>;
}

export const DealTaxesLayoutContext = createContext<DealTaxesLayoutContextType | null>(null);

const DealTaxesLayout = () => {
    const location = useLocation();
    const isExpandedView = location.pathname.includes('/expanded');

    const [selectedData, setSelectedData] = useState<DealTaxesSelectedData>({
        selectedInvestor: undefined,
        assets: [],
        investors: [],

        investmentFund: {
            id: undefined,
            name: undefined,
            countryCode: undefined,
            countryName: undefined,
            countryId: undefined,
            legalTypeId: undefined,
            legalFormId: undefined,
            legalFormShort: undefined,
        },
        fundOfFunds: undefined,

        openEnded: false,
        // corporate: false,
        booked: undefined,
        ifBusinessAssets: undefined,
        ifCommercialPartnership: undefined,
        fofOpenEnded: undefined,
        // fofCorporate: undefined,
        fofIfCommercialPartnership: undefined,

        ukInvesting: undefined,
        fofUkInvesting: undefined,

        // for tax optimisation
        // fundStatus: undefined,
    });

    const [setAssets, setInvestmentFunds, setInvestors, getCountries, setFundOfFunds, setGraphStoreData] =
        useDiagramStore((state) => [
            state.setAssets,
            state.setInvestmentFunds,
            state.setInvestors,
            state.getCountries,
            state.setFundOfFunds,
            state.setStoreData,
        ]);

    const [isFoF, setIsFoF] = useState(false);

    const [initialInvestment, setInitialInvestment] = useState(100);
    const [returnOnAsset, setReturnOnAsset] = useState(20);
    const [distribution, setDistribution] = useState(10);

    const [rawData, setRawData] = useState<DiagramData | null>(null);

    const [showPopup, setShowPopup] = useState(false);
    const [pageReady, setPageReady] = useState(false);

    const [checkClicked, setCheckClicked] = useState(false);
    const [toggleRequest, setToggleRequest] = useState(false);

    const [tableUrl, setTableUrl] = useState('');
    const [optimisationUrl, setOptimisationUrl] = useState('');

    const [isOptimisation, setIsOptimisation] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();

    const [fund, setFund] = useState({
        country: '',
        fund: '',
    });

    const [fofLegalTypesDisabled, setFofLegalTypesDisabled] = useState<boolean | undefined>(undefined);
    const [fofOpenEndedDisabled, setFofOpenEndedDisabled] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (!isExpandedView && pageReady && !showPopup && searchParams.toString().length) {
            setToggleRequest(true);
        }
    }, [isExpandedView]);

    useEffect(() => {
        if (showPopup) {
            setPageReady(true);
            dispatchAsidePortalContentChanged(false);
        } else {
            // dispatchAsidePortalContentChanged(true);
        }
    }, [showPopup]);

    useEffect(() => {
        setIsFoF(!!selectedData.fundOfFunds);
    }, [selectedData.fundOfFunds]);

    useEffect(() => {
        if (!searchParams.toString().length) {
            (async () => {
                await initPage();
            })();
        }
    }, [searchParams]);

    useEffect(() => {
        setGraphStoreData({
            useInvestmentStructure: true,
            graphType: GraphTypes.Taxes,
        });
        (async () => {
            await initPage();
        })();
    }, []);

    const initPage = async () => {
        await getCountries();

        const { data, optimisation, initialInvestmentParam, returnOnAssetParam, distributionParam, openTaxIndex } =
            await getDealTaxesUrlParams(searchParams);

        setIsOptimisation(optimisation === 'true');

        if (!data) {
            setShowPopup(true);
            return;
        }

        if (searchParams.get('preview')) {
            setSelectedData({
                ...selectedData,
                ...data,
            });
            setShowPopup(true);
            return;
        }

        // optimisation
        if (optimisation && initialInvestmentParam && returnOnAssetParam && distributionParam) {
            setInitialInvestment(+initialInvestmentParam);
            setReturnOnAsset(+returnOnAssetParam);
            setDistribution(+distributionParam);

            setFund({
                country: data.investmentFund.countryCode!,
                fund: data.investmentFund.name!,
            });

            setOptimisationUrl(
                new URLSearchParams({
                    initialInvestment: initialInvestmentParam,
                    returnOnAsset: returnOnAssetParam,
                    distribution: distributionParam,
                    // currentInvestor: data.currentInvestor,
                    // investorCountry: data.investorCountry,
                    // assetCountry: data.assetCountry,
                    // currentAsset: data.currentAsset,
                    openTaxIndex: openTaxIndex ? openTaxIndex : '',
                }).toString(),
            );
        }

        for (const item of data.investors) {
            setInvestors(item.countryCode!);
        }
        for (const item of data.assets) {
            setAssets(item.countryCode!);
        }
        setInvestmentFunds(data.investmentFund.countryCode!);

        if (data.fundOfFunds) {
            setFundOfFunds(data.fundOfFunds.countryCode!);
        }

        setSelectedData({
            ...selectedData,
            ...data,
        });

        setCheckClicked(true);
        setPageReady(true);
    };

    const handleCheckbox = (prop: any) => async (event: any) => {
        const checked = event.target.checked;
        const tmp = { ...selectedData, [prop]: checked };

        setUrlParams(tmp);

        setSelectedData({ ...tmp });

        setCheckClicked(true);
    };

    const setUrlParams = (selectedData: DealTaxesSelectedData) => {
        const tableUrl = new URLSearchParams();

        for (const entry of Object.entries(selectedData)) {
            // @ts-ignore
            if (typeof entry[1] !== 'undefined' && entry[1] !== 'undefined') {
                tableUrl.set(entry[0], JSON.stringify(entry[1]));
            }
        }

        if (isOptimisation) {
            tableUrl.set('optimisation', `${isOptimisation}`);
            tableUrl.set('initialInvestment', `${initialInvestment}`);
            tableUrl.set('returnOnAsset', `${returnOnAsset}`);
            tableUrl.set('distribution', `${distribution}`);
            // tableUrl.set('investmentFund', `${fund.fund}`);
        }

        setTableUrl('?' + tableUrl.toString());

        if (!isOptimisation) {
            setSearchParams(tableUrl.toString());

            return;
        }
    };

    const setSelectedInvestor = (investor: SelectedItemExtended) => {
        setSelectedData({
            ...selectedData,
            selectedInvestor: investor,
        });

        setToggleRequest(true);
    };

    const [onEditHandler, setOnEditHandler] = useState<() => void>(() => {
        return () => {
            setShowPopup(true);
            dispatchAsidePortalContentChanged(false);
        };
    });

    const handleEdit = () => {
        onEditHandler();
    };

    const setOnEdit = (handler: () => void) => {
        setOnEditHandler(() => handler);
    };

    const backButton = isOptimisation ? (
        <LinkedButton
            to={{
                pathname: '/tax-optimisation',
                search: searchParams.toString(),
            }}
            after={<Icon icon={Icons.return} />}
            color={'neutral'}
            size={'sm'}
        >
            Back
        </LinkedButton>
    ) : undefined;

    // Create context value
    const contextValue: DealTaxesLayoutContextType = {
        // Info props (lines 552-555)
        data: rawData?.nav,
        initialInvestment: initialInvestment,
        returnOnAsset: returnOnAsset,
        distribution: distribution,
        inAside: true,

        // Menu props (lines 566-591)
        selectedData: selectedData,
        setSelectedData: setSelectedData,
        setSelectedInvestor: setSelectedInvestor,
        active: true,
        hidden: false,
        onEdit: handleEdit,
        setOnEdit: setOnEdit,
        backButton: backButton,

        checkClicked,
        setCheckClicked,

        toggleRequest,
        setToggleRequest,

        // Popup control
        showPopup: showPopup,
        setShowPopup: setShowPopup,

        // Additional props from checksProps
        handleCheckbox: handleCheckbox,
        isFoF: isFoF,
        fofLegalTypesDisabled: fofLegalTypesDisabled,
        fofOpenEndedDisabled: fofOpenEndedDisabled,
        isOptimisation: isOptimisation,

        setFofLegalTypesDisabled,
        setFofOpenEndedDisabled,

        // Route info
        isExpandedView: isExpandedView,

        // Fund and optimisation
        fund: fund,
        optimisationUrl: optimisationUrl,

        // Page state
        pageReady: pageReady,
        setPageReady: setPageReady,
        tableUrl: tableUrl,
        setTableUrl: setTableUrl,

        // Data fetching
        // getData: getData,

        rawData,
        setRawData,
    };

    return (
        <DealTaxesLayoutContext.Provider value={contextValue}>
            {
                // !showPopup &&
                // !isOptimisation &&
                document.querySelector('#menuAsideContent') &&
                    createPortal(
                        <DealTaxesAsideMenu
                            infoProps={{
                                data: rawData?.nav,
                                initialInvestment: initialInvestment,
                                returnOnAsset: returnOnAsset,
                                distribution: distribution,
                                inAside: true,
                            }}
                            checksProps={{
                                handleCheckbox: handleCheckbox,
                                selectedData: selectedData,
                                isFoF: isFoF,
                                fofLegalTypesDisabled: fofLegalTypesDisabled,
                                fofOpenEndedDisabled: fofOpenEndedDisabled,
                            }}
                            isOptimisation={isOptimisation}
                            selectedData={selectedData}
                            setSelectedInvestor={setSelectedInvestor}
                            active={true}
                            // active={isMenuOpen}
                            // setActive={setIsMenuOpen}
                            // hidden={showPopup}
                            hidden={false}
                            onEdit={handleEdit}
                            backButton={backButton}
                        />,
                        document.querySelector('#menuAsideContent')!,
                    )
            }

            <Outlet context={contextValue} />
        </DealTaxesLayoutContext.Provider>
    );
};

export default DealTaxesLayout;
